import { useState, useEffect } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Building2, MapPin, Phone, Mail, Globe, Loader2, Send, ArrowRight, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtPrice(n, currency = 'MXN') {
  if (!n) return '$0';
  return `$${Number(n).toLocaleString('es-MX')} ${currency}`;
}

export default function PublicLandingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/public/l/${slug}`)
      .then(async r => {
        if (r.data?.company?.custom_html_path) {
          try {
            const htmlRes = await axios.get(`${API}${r.data.company.custom_html_path}`, { responseType: 'text', transformResponse: [(d) => d] });
            document.open();
            document.write(htmlRes.data);
            document.close();
            return;
          } catch (err) {
            setError('No se pudo cargar el landing personalizado');
            setLoading(false);
            return;
          }
        }
        if (r.data?.company?.business_type === 'car_dealer') {
          window.location.replace(`/car-sites/${slug}`);
          return;
        }
        setData(r.data);
        setLoading(false);
      })
      .catch(e => { setError(e.response?.data?.detail || 'Empresa no encontrada'); setLoading(false); });
  }, [slug]);

  const submit = async () => {
    if (!contactForm.name || (!contactForm.email && !contactForm.phone)) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/l/${slug}/contact`, { ...contactForm, property_id: selectedProperty });
      setSubmitted(true);
    } catch { alert('Error al enviar'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0a0c12] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#0a0c12] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center" data-testid="public-landing-404">
        <div className="w-20 h-20 mx-auto rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6">
          <Building2 className="w-10 h-10 text-white/30" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-white mb-3">404</h1>
        <p className="text-white/80 text-sm font-medium mb-2">Este landing no esta disponible</p>
        <p className="text-muted-foreground text-xs leading-relaxed">
          La empresa puede haber desactivado su pagina publica o el enlace no es correcto.
          Verifica la URL o contacta a la empresa directamente.
        </p>
        <p className="text-[10px] text-white/20 mt-6">Powered by {APP_NAME} CRM</p>
      </div>
    </div>
  );

  const c = data.company;
  const primary = c.primary_color || '#A5D62C';
  const secondary = c.secondary_color || '#1B5E7B';
  const tertiary = c.tertiary_color || '#0a0c12';
  const logoUrl = c.logo_url ? (c.logo_url.startsWith('http') ? c.logo_url : `${API}${c.logo_url}`) : '';
  const heroUrl = c.landing_hero_image ? (c.landing_hero_image.startsWith('http') ? c.landing_hero_image : `${API}${c.landing_hero_image}`) : '';

  return (
    <div data-testid="public-landing" style={{ backgroundColor: tertiary, color: '#fff', minHeight: '100vh' }}>
      {/* Nav */}
      <nav className="py-4 px-6 border-b border-white/5 sticky top-0 backdrop-blur-md z-40" style={{ backgroundColor: `${tertiary}cc` }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? <img src={logoUrl} alt={c.name} className="h-10 object-contain" /> : <Building2 className="w-8 h-8" style={{ color: primary }} />}
            <div>
              <p className="font-heading text-sm font-bold">{c.name}</p>
              <p className="text-[10px] opacity-60 uppercase tracking-wider capitalize">{c.business_type}</p>
            </div>
          </div>
          <a href="#contacto" className="text-[11px] px-4 py-2 rounded-full font-bold" style={{ backgroundColor: primary, color: '#000' }} data-testid="landing-nav-cta">
            {c.landing_cta_text || 'Contactar'}
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-20 px-6 overflow-hidden">
        {heroUrl && (
          <div className="absolute inset-0 z-0">
            <img src={heroUrl} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${tertiary}e0, ${secondary}40)` }} />
          </div>
        )}
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            {c.landing_headline || `Bienvenido a ${c.name}`}
          </h1>
          <p className="text-base max-w-2xl mx-auto opacity-80 leading-relaxed mb-8" data-testid="landing-description">
            {c.landing_description || c.description || `Descubre las mejores opciones con ${c.name}.`}
          </p>
          <a href="#contacto" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-sm" style={{ backgroundColor: primary, color: '#000' }} data-testid="landing-hero-cta">
            {c.landing_cta_text || 'Conoce mas'} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Properties */}
      {data.properties && data.properties.length > 0 && (
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl font-bold mb-8">Nuestro inventario</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.properties.map(p => (
                <div
                  key={p.id}
                  className="rounded-2xl overflow-hidden border border-white/5 bg-white/[0.02] hover:border-white/20 transition-colors cursor-pointer"
                  onClick={() => { setSelectedProperty(p.id); document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' }); }}
                  data-testid={`landing-property-${p.id}`}
                >
                  {p.images?.[0] && <img src={p.images[0].startsWith('http') ? p.images[0] : `${API}${p.images[0]}`} alt="" className="w-full h-44 object-cover" />}
                  <div className="p-4">
                    <p className="text-xs font-bold mb-1 truncate">{p.title}</p>
                    <p className="text-[10px] opacity-60 flex items-center gap-1 mb-2">
                      <MapPin className="w-2.5 h-2.5" /> {p.city}, {p.state}
                    </p>
                    <p className="text-sm font-bold mb-2" style={{ color: primary }}>{fmtPrice(p.price, p.currency)}</p>
                    <div className="flex gap-3 text-[10px] opacity-70">
                      <span>{p.bedrooms}h</span>
                      <span>{p.bathrooms}b</span>
                      <span>{p.area_m2}m²</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contacto */}
      <section id="contacto" className="py-16 px-6" style={{ backgroundColor: `${primary}08` }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl font-bold mb-2 text-center">Contactanos</h2>
          <p className="text-sm opacity-70 text-center mb-8">Nuestro equipo te atendera en menos de 24 horas.</p>

          {submitted ? (
            <div className="p-8 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-center" data-testid="landing-submitted">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
              <p className="text-white font-semibold mb-1">Gracias por contactarnos</p>
              <p className="text-xs text-muted-foreground">Un asesor te escribira pronto.</p>
            </div>
          ) : (
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Nombre *"
                  value={contactForm.name}
                  onChange={e => setContactForm({ ...contactForm, name: e.target.value })}
                  className="bg-black/30 border border-white/10 text-sm px-3 py-2.5 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-white/30"
                  data-testid="landing-name-input"
                />
                <input
                  type="tel"
                  placeholder="Telefono"
                  value={contactForm.phone}
                  onChange={e => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="bg-black/30 border border-white/10 text-sm px-3 py-2.5 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-white/30"
                  data-testid="landing-phone-input"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={contactForm.email}
                  onChange={e => setContactForm({ ...contactForm, email: e.target.value })}
                  className="md:col-span-2 bg-black/30 border border-white/10 text-sm px-3 py-2.5 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-white/30"
                  data-testid="landing-email-input"
                />
                <textarea
                  placeholder="Mensaje (opcional)"
                  value={contactForm.message}
                  onChange={e => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={3}
                  className="md:col-span-2 bg-black/30 border border-white/10 text-sm px-3 py-2.5 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 resize-none"
                  data-testid="landing-message-input"
                />
              </div>
              {selectedProperty && (
                <p className="text-[11px] opacity-60">Inmueble seleccionado: <code className="bg-white/5 px-1.5 py-0.5 rounded">{selectedProperty}</code></p>
              )}
              <button
                onClick={submit}
                disabled={submitting || !contactForm.name || (!contactForm.email && !contactForm.phone)}
                className="w-full py-3 rounded-lg font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2"
                style={{ backgroundColor: primary, color: '#000' }}
                data-testid="landing-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar mensaje
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto text-center space-y-3">
          <div className="flex items-center justify-center gap-5 text-xs opacity-70 flex-wrap">
            {c.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.contact_phone}</span>}
            {c.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.contact_email}</span>}
            {c.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.address}</span>}
            {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:opacity-100"><Globe className="w-3 h-3" /> Sitio web</a>}
          </div>
          <p className="text-[10px] opacity-40">Powered by {APP_NAME} CRM</p>
          <div className="text-center mt-4 text-sm text-white/50 space-x-4">
            <Link to="/terminos" data-testid="public-landing-terminos-link">Términos y Condiciones</Link>
            <span>·</span>
            <Link to="/privacidad" data-testid="public-landing-privacidad-link">Aviso de Privacidad</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
